import { CommonModule } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { IonApp, IonSplitPane, IonMenu, IonContent, IonList, IonListHeader, IonNote, IonMenuToggle, IonItem, IonIcon, IonLabel, IonRouterOutlet, IonRouterLink } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { bookmarkOutline, bookmarkSharp, documentTextOutline, folderOpenOutline, logInOutline, moonOutline, musicalNotesOutline, optionsOutline, sunnyOutline } from 'ionicons/icons';
import { filter } from 'rxjs';
import { BrowserLogService } from './services/browser-log.service';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [CommonModule, RouterLink, RouterLinkActive, IonApp, IonSplitPane, IonMenu, IonContent, IonList, IonListHeader, IonNote, IonMenuToggle, IonItem, IonIcon, IonLabel, IonRouterLink, IonRouterOutlet],
})
export class AppComponent {
  private readonly themeStorageKey = 'rachana-theme';
  private readonly mobileLandscapeMaxWidth = 1024;
  private readonly platform = Capacitor.getPlatform();
  public appPages = [
    { title: 'Composition', url: '/composition', icon: 'bookmark-outline' },
    { title: 'Song Composition', url: '/song-composition', icon: 'musical-notes-outline' },
    { title: 'Taals', url: '/taal', icon: 'musical-notes-outline' }
  ];
  readonly utilityPages = [
    ...(environment.enableBrowserLogCapture ? [{ title: 'Debug Logs', url: '/debug-logs', icon: 'document-text-outline' }] : []),
    { title: 'Synth Check', url: '/synthCheck', icon: 'options-outline' },
    { title: 'SF2 Synth Check', url: '/sf2SynthCheck', icon: 'options-outline' },
    { title: 'LRCGEN', url: '/lrcgen', icon: 'document-text-outline' }
  ];
  isLightTheme = false;
  requireLandscape = false;
  utilityFolderOpen = false;
  breadcrumbs = ['Composition'];
  private webLandscapeAlertOpen = false;

  constructor(
    private readonly router: Router,
    private readonly browserLog: BrowserLogService
  ) {
    this.browserLog.install();
    addIcons({ bookmarkOutline, bookmarkSharp, documentTextOutline, folderOpenOutline, musicalNotesOutline, logInOutline, optionsOutline, sunnyOutline, moonOutline });
    this.isLightTheme = localStorage.getItem(this.themeStorageKey) === 'light';
    this.applyTheme();
    this.updateBreadcrumbs(this.router.url);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.updateBreadcrumbs(event.urlAfterRedirects));
    this.updateOrientationState();
    void this.tryLockLandscape();
  }

  toggleTheme(): void {
    this.isLightTheme = !this.isLightTheme;
    localStorage.setItem(this.themeStorageKey, this.isLightTheme ? 'light' : 'dark');
    this.applyTheme();
  }

  toggleUtilityFolder(): void {
    this.utilityFolderOpen = !this.utilityFolderOpen;
  }

  get currentPageLabel(): string {
    return this.breadcrumbs[this.breadcrumbs.length - 1] ?? 'Composition';
  }

  private applyTheme(): void {
    document.documentElement.classList.toggle('app-light-theme', this.isLightTheme);
  }

  private updateBreadcrumbs(url: string): void {
    const path = url.split('?')[0].split('#')[0];
    const segments = path.split('/').filter(Boolean);
    if (!segments.length || segments[0] === 'composition') {
      this.breadcrumbs = ['Composition', ...this.getDetailCrumbs(segments)];
      return;
    }

    const labels: Record<string, string> = {
      'song-composition': 'Song Composition',
      taal: 'Taals',
      'debug-logs': 'Debug Logs',
      'synth-check': 'Synth Check',
      synthCheck: 'Synth Check',
      'sf2-synth-check': 'SF2 Synth Check',
      sf2SynthCheck: 'SF2 Synth Check',
      lrcgen: 'LRCGEN',
      user: 'User',
      play: 'Play',
      share: 'Share',
      about: 'About',
      help: 'Help',
      support: 'Support'
    };
    this.breadcrumbs = [labels[segments[0]] ?? this.toTitle(segments[0]), ...this.getDetailCrumbs(segments)];
  }

  private getDetailCrumbs(segments: string[]): string[] {
    if (segments[0] === 'song-composition' && segments.includes('piece')) {
      const songCrumb = segments[1] === 'new' ? 'New Song' : 'Selected Song';
      return [songCrumb, 'Musical Piece'];
    }

    if (segments[0] === 'song-composition' && segments[1] && segments[1] !== 'new') {
      return ['Selected Song'];
    }

    return segments.slice(1).map((segment) => {
      if (segment === 'new') {
        return segments[0] === 'song-composition' ? 'New Song' : 'New';
      }
      return this.toTitle(segment);
    });
  }

  private toTitle(value: string): string {
    return value
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  @HostListener('window:resize')
  @HostListener('window:orientationchange')
  onViewportChange(): void {
    this.updateOrientationState();
    void this.tryLockLandscape();
    void this.maybeAlertForWebLandscape();
  }

  private updateOrientationState(): void {
    if (typeof window === 'undefined') {
      this.requireLandscape = false;
      return;
    }

    const width = window.innerWidth;
    const height = window.innerHeight;
    const isPortableViewport = Math.min(width, height) <= this.mobileLandscapeMaxWidth;
    this.requireLandscape = this.platform !== 'web' && isPortableViewport && height > width;
  }

  private async tryLockLandscape(): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }

    const width = window.innerWidth;
    const height = window.innerHeight;
    const isPortableViewport = Math.min(width, height) <= this.mobileLandscapeMaxWidth;
    if (!isPortableViewport) {
      return;
    }

    const orientationApi = (screen as Screen & {
      orientation?: { lock?: (orientation: string) => Promise<void> };
    }).orientation;

    if (!orientationApi?.lock) {
      return;
    }

    try {
      await orientationApi.lock('landscape');
    } catch {
      // Browser support is limited outside installed/fullscreen contexts.
    }
  }

  private async maybeAlertForWebLandscape(): Promise<void> {
    if (this.platform !== 'web' || typeof window === 'undefined') {
      return;
    }

    const width = window.innerWidth;
    const height = window.innerHeight;
    const isPortableViewport = Math.min(width, height) <= this.mobileLandscapeMaxWidth;
    const shouldAlert = isPortableViewport && height > width;

    if (!shouldAlert || this.webLandscapeAlertOpen) {
      return;
    }

    this.webLandscapeAlertOpen = true;
    try {
      window.alert('For better viewing on small screens, switch to landscape.');
    } finally {
      this.webLandscapeAlertOpen = false;
    }
  }
}
